/**
 * FindingPreviewModal — expanded slice viewer for a single gallery finding.
 *
 * Opens when the user clicks a tile in PreviewGalleryView. Shows:
 *  - A large preview of the finding's slices with a wider window (±RADIUS_EXPANDED slices)
 *  - A scrubber (range input + mouse-wheel) to move through slices manually
 *  - Slice counter "i / N"
 *  - Finding caption and the standard non-diagnostic disclaimer
 *  - Close on Escape, backdrop click, or close button
 *
 * NO markings on pixels — windowing + scrub only (510(k) constraint).
 */

import React, { useEffect, useRef, useState, useCallback } from "react";
import { useFindingSliceWindow } from "../hooks/useFindingSliceWindow";
import { formatFindingName } from "../utils/textFormatters";
import { hasDot, priorityLabel } from "../utils/findingPriority";
import type { GalleryFinding } from "../hooks/useStudyQueries";

// Wider window for the modal — load ±12 neighbors around the key slice (up to 25 frames)
export const RADIUS_EXPANDED = 12;

interface FindingPreviewModalProps {
  finding: GalleryFinding;
  studyId: string;
  deviceId: string;
  onClose: () => void;
}

export const FindingPreviewModal: React.FC<FindingPreviewModalProps> = ({
  finding,
  studyId,
  deviceId,
  onClose,
}) => {
  const { frames, keyIndex, loading } = useFindingSliceWindow(
    studyId,
    deviceId,
    finding.condition,
    finding.topSlice,
    RADIUS_EXPANDED,
    true,
  );

  // Start scrubber at the key slice; update when the window grows
  const [sliceIndex, setSliceIndex] = useState(0);
  useEffect(() => {
    setSliceIndex(keyIndex);
  }, [keyIndex]);

  // Clamp on frames length change (window grows as neighbors load)
  useEffect(() => {
    setSliceIndex((prev) => Math.min(prev, Math.max(0, frames.length - 1)));
  }, [frames.length]);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  // Mouse-wheel scrub over the image area
  const imageAreaRef = useRef<HTMLDivElement>(null);
  const handleWheel = useCallback(
    (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (frames.length <= 1) return;
      setSliceIndex((prev) => {
        const delta = e.deltaY > 0 ? 1 : -1;
        return Math.max(0, Math.min(frames.length - 1, prev + delta));
      });
    },
    [frames.length],
  );

  useEffect(() => {
    const el = imageAreaRef.current;
    if (!el) return;
    el.addEventListener("wheel", handleWheel, { passive: false });
    return () => el.removeEventListener("wheel", handleWheel);
  }, [handleWheel]);

  const segments = finding.condition.split("/");
  const name = formatFindingName(segments[1] ?? segments[0]);
  const organ = formatFindingName(segments[0]);

  const currentUrl = frames[sliceIndex] ?? null;
  const total = frames.length;

  return (
    /* Overlay */
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
      onClick={(e) => {
        // Close on backdrop click (not when clicking the modal itself)
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label={`Expanded preview: ${name}`}
    >
      {/* Modal panel */}
      <div className="relative flex w-full max-w-xl flex-col gap-4 rounded-sharp border border-border bg-bg p-6 shadow-xl">
        {/* Close button */}
        <button
          onClick={onClose}
          aria-label="Close expanded preview"
          className="absolute right-4 top-4 flex h-7 w-7 items-center justify-center rounded-sharp border border-border text-fg-dim transition-colors hover:border-fg-dim hover:text-fg"
        >
          ✕
        </button>

        {/* Finding caption */}
        <div className="flex items-start gap-2 pr-10">
          {hasDot(finding.category) && (
            <span
              className="mt-[4px] h-2 w-2 flex-none rounded-full bg-signal"
              aria-hidden="true"
            />
          )}
          <div>
            <div className="font-mono text-[9px] uppercase leading-tight tracking-[0.12em] text-fg-dim">
              {organ}
              {priorityLabel(finding.category) ? ` · ${priorityLabel(finding.category)}` : ""}
            </div>
            <div className="text-[15px] font-medium leading-snug text-fg">{name}</div>
          </div>
        </div>

        {/* Large image area — aspect-square, no markings */}
        <div
          ref={imageAreaRef}
          className="relative aspect-square w-full overflow-hidden rounded-sharp border border-border bg-black"
          style={{ cursor: frames.length > 1 ? "ns-resize" : "default" }}
        >
          {currentUrl ? (
            <img
              src={currentUrl}
              alt={`${name} slice ${sliceIndex + 1} of ${total}`}
              className="h-full w-full object-contain"
              draggable={false}
            />
          ) : loading ? (
            <div className="flex h-full w-full items-center justify-center bg-bg-2">
              <div
                role="status"
                aria-label="Loading preview"
                className="h-10 w-10 animate-spin rounded-full border-4 border-signal border-t-transparent"
              />
            </div>
          ) : (
            <div className="flex h-full items-center justify-center">
              <span className="font-mono text-[10px] text-fg-faint">no preview</span>
            </div>
          )}
        </div>

        {/* Scrubber + counter */}
        {(frames.length > 1 || loading) && (
          <div className="flex flex-col gap-1">
            <input
              type="range"
              min={0}
              max={Math.max(0, total - 1)}
              value={sliceIndex}
              onChange={(e) => setSliceIndex(Number(e.target.value))}
              disabled={frames.length <= 1}
              aria-label="Slice scrubber"
              className="w-full accent-signal"
            />
            <div className="text-right font-mono text-[10px] text-fg-dim">
              slice {total > 0 ? sliceIndex + 1 : "—"} / {total > 0 ? total : "—"}
            </div>
          </div>
        )}

        {/* Non-diagnostic disclaimer */}
        <div className="flex justify-end">
          <span className="rounded-sharp border border-border px-1.5 py-0.5 font-mono text-[9px] text-fg-faint">
            Informational preview · not for primary diagnosis
          </span>
        </div>
      </div>
    </div>
  );
};
